import { Component, computed, effect, inject, OnInit, signal, viewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { EMPTY } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { tryGetErrorMessage } from '../../../../error.utils';
import { SELF_SERVICE_ASSIGNMENT_QUERY_PARAMS } from '../../../../helpers';
import { AssignmentService } from '../../../../service/assignment.service';
import { LoggedInCallback } from '../../../../service/cognito.service';
import { LossPreventionService } from '../../../../service/loss-prevention.service';
import { UserLoginService } from '../../../../service/user-login.service';
import { LocationTiny, ReviewAssignmentDto } from '../../../../types';
import { PageSelectLocationComponent } from '../../../components/page-select-location/page-select-location.component';

enum AssignmentState {
  /**
   * maybe the user doesn't have the right role?
   */
  Authenticating,
  /**
   * check if there is an existing assignment.
   * if so, redirect to it immediately
   */
  CheckingExisting,
  /**
   * there is an existing assignment.
   * redirect now
   */
  HasExisting,
  /**
   * check if there are any available assignments.
   * if so, the user must decide if they want to start
   */
  CheckingAvailable,
  /**
   * there are available assignments.
   * again, the user must decide if they want to start
   */
  HasAvailable,
  /**
   * the user has decided to start
   */
  Starting,
  /**
   * unfortunately, there are no available assignments.
   * something wrong with the queue?
   */
  NoAvailable,
  /**
   * something went wrong, show the error message
   */
  Error,
}

@Component({
  selector: 'app-review-check-assignment',
  templateUrl: './review-check-assignment.component.html',
  styleUrl: './review-check-assignment.component.scss',
  standalone: false,
})
export class ReviewCheckAssignmentComponent implements OnInit, LoggedInCallback {
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private userService = inject(UserLoginService);
  private assignmentService = inject(AssignmentService);
  private lossPreventionService = inject(LossPreventionService);
  private locationSelect = viewChild<PageSelectLocationComponent>('locationSelect');

  public readonly State = AssignmentState;
  public readonly state = signal<AssignmentState>(AssignmentState.Authenticating);
  public readonly errorMessage = signal<string>('');
  public existingAssignment = signal<ReviewAssignmentDto | null>(null);

  public location = signal<LocationTiny | undefined>(undefined);
  public locationId = computed(() => this.location()?.id);

  constructor() {
    this.userService.isAuthenticated(this);
    this.setupLocationFromUrl();
  }

  ngOnInit() {
    this.hideLauncher();
    this.state.set(AssignmentState.Authenticating);
    this.userService
      .getMe()
      .pipe(
        catchError((err) => {
          this.errorMessage.set(`An error occurred while authenticating: ${tryGetErrorMessage(err)}.`);
          this.state.set(AssignmentState.Error);
          return EMPTY;
        }),
      )
      .subscribe(() => this.checkAssignment());
  }

  /**
   * this method does the following in sequence:
   * 1. check if the user has an existing assignment
   * 2. check if there are any available assignments
   * 3. if no available assignments,
   *    there might be a problem (empty queue, or FE/BE issue).
   * 3.a. the user can click the button to search again
   */
  checkAssignment() {
    if (this.state() === AssignmentState.HasExisting) return;
    this.state.set(AssignmentState.CheckingExisting);
    this.errorMessage.set('');
    this.existingAssignment.set(null);
    this.lossPreventionService
      .checkExistingAssignment(this.locationId())
      .pipe(
        switchMap((existing) => {
          if (existing) {
            this.existingAssignment.set(existing);
            this.state.set(AssignmentState.HasExisting);
            return EMPTY;
          }
          this.state.set(AssignmentState.CheckingAvailable);
          return this.lossPreventionService.hasAnyAvailableAssignments(this.locationId());
        }),
        tap((available) => {
          this.state.set(available?.has_any_reviews ? AssignmentState.HasAvailable : AssignmentState.NoAvailable);
        }),
        catchError((err) => {
          if (err.status === 403) {
            this.errorMessage.set(`You don't have permissions to start a new review.`);
          } else {
            this.errorMessage.set(`An error occurred while checking assignments: ${tryGetErrorMessage(err)}.`);
          }
          this.state.set(AssignmentState.Error);
          return EMPTY;
        }),
      )
      .subscribe();
  }

  nextAssignment() {
    this.errorMessage.set('');
    this.state.set(AssignmentState.Starting);
    this.assignmentService.getNextAssignment(this.locationId()).subscribe(
      (assignment) => this.redirectToReview(assignment.assignment_id),
      (err) => {
        if (err.message === 'No assignments available') {
          this.state.set(AssignmentState.NoAvailable);
        } else {
          this.errorMessage.set(tryGetErrorMessage(err));
          this.state.set(AssignmentState.Error);
        }
      },
    );
  }

  continueExistingAssignment() {
    const assignment = this.existingAssignment();
    if (!assignment) return;
    this.redirectToReview(assignment.review_id);
  }

  public onLocationChange(location: LocationTiny[] | LocationTiny | null) {
    if (!location) {
      this.location.set(undefined);
      this.router.navigate([], {
        queryParams: { [SELF_SERVICE_ASSIGNMENT_QUERY_PARAMS.LOCATION_ID]: null },
        queryParamsHandling: 'merge',
      });
      return;
    }
    const selectedLocation = location as LocationTiny;
    this.location.set(selectedLocation);
    this.router.navigate([], {
      queryParams: { [SELF_SERVICE_ASSIGNMENT_QUERY_PARAMS.LOCATION_ID]: selectedLocation.id },
      queryParamsHandling: 'merge',
    });
    this.checkAssignment();
  }

  private setupLocationFromUrl() {
    const locationId = this.route.snapshot.queryParams[SELF_SERVICE_ASSIGNMENT_QUERY_PARAMS.LOCATION_ID];
    if (!locationId) return;
    effect(
      () => {
        const locationSelect = this.locationSelect();
        if (locationSelect && locationSelect.locations().length > 0) {
          const foundLocation = locationSelect.locations().find((loc) => loc.id === +locationId);
          if (foundLocation) this.location.set(foundLocation);
        }
      },
      { allowSignalWrites: true },
    );
  }

  isLoggedIn(_: string, loggedIn: boolean): void {
    if (!loggedIn) this.router.navigate(['/home/login']);
  }

  private hideLauncher(): void {
    document.body.classList.add('hide-launcher');
  }

  private redirectToReview(reviewId: string) {
    if (!reviewId) return;

    const baseUrl = this.router.url.split('?')[0];

    this.router.navigate([`${baseUrl}/${reviewId}`], { queryParamsHandling: 'preserve' });
  }
}
